interface Hsl {
  h: number;
  s: number;
  l: number;
}

const HSL_PATTERN = /^\s*(-?[\d.]+)(?:deg)?\s+([\d.]+)%\s+([\d.]+)%\s*$/;

export const parseHsl = (value: string): Hsl | null => {
  const match = HSL_PATTERN.exec(value);
  if (!match) return null;
  return { h: Number(match[1]), s: Number(match[2]), l: Number(match[3]) };
};

const hslToRgb = ({ h, s, l }: Hsl): [number, number, number] => {
  const sat = s / 100;
  const light = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sat * Math.min(light, 1 - light);
  const channel = (n: number) =>
    light - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [channel(0), channel(8), channel(4)];
};

const relativeLuminance = (hsl: Hsl): number => {
  const [r, g, b] = hslToRgb(hsl).map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const contrastRatio = (a: Hsl, b: Hsl): number => {
  const [lighter, darker] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (lighter + 0.05) / (darker + 0.05);
};

const WHITE: Hsl = { h: 0, s: 0, l: 100 };

export const ensureContrastOnWhite = (value: string, minRatio = 3): string => {
  const parsed = parseHsl(value);
  if (!parsed) return `hsl(${value})`;

  let { l } = parsed;
  while (l > 0 && contrastRatio({ ...parsed, l }, WHITE) < minRatio) {
    l -= 1;
  }
  return `hsl(${parsed.h}deg ${parsed.s}% ${l}%)`;
};
